import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaCheckDouble,
  FaClock,
  FaFilePdf,
  FaFileAlt,
  FaFileInvoice,
  FaQuoteRight,
  FaQuestionCircle,
  FaUserCheck,
  FaExchangeAlt,
  FaTimes,
  FaRedoAlt,
} from "react-icons/fa";
import "./WhatsAppChat.css";

const MessageArea = ({
  activeConversation,
  messages = [],
  isLoading,
  onOpenPullModal,
  onOpenPushModal,
  onImageClick,
  onCloseChat,
  onReopenChat,
}) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeConversation]);

  if (!activeConversation) {
    return (
      <div className="rekonic-center-chat-area" style={{ alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", color: "#64748b" }}>
          <div style={{ fontSize: "42px", marginBottom: "10px" }}>💬</div>
          <h3 style={{ margin: "0 0 6px", color: "#0f172a" }}>Rekonic WhatsApp Inbox</h3>
          <p style={{ margin: 0, fontSize: "13px" }}>Select a conversation or start a new chat to begin messaging.</p>
        </div>
      </div>
    );
  }

  const isCrm = activeConversation?.crm_customer?.is_linked;
  const displayName =
    activeConversation?.crm_customer?.customer_name ||
    activeConversation?.contact?.name ||
    activeConversation?.contact?.phone ||
    "Customer";
  const phone = activeConversation?.contact?.phone || "";

  const handleCreateQuotation = () => {
    navigate("/dashboard/quotations/new", {
      state: {
        customer_name: displayName,
        phone: phone,
      },
    });
  };

  const handleCreateInvoice = () => {
    navigate("/dashboard/sales/new", {
      state: {
        customer_name: displayName,
        customer_phone: phone,
      },
    });
  };

  const handleCreateInquiry = () => {
    navigate("/dashboard/inquiries/new", {
      state: {
        customer_name: displayName,
        phone: phone,
      },
    });
  };

  const formatMessageTime = (ts) => {
    if (!ts) return "";
    try {
      const d = new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  const renderStatusIcon = (status) => {
    if (status === "read") {
      return <FaCheckDouble className="rekonic-check-icon read" title="Read" />;
    }
    if (status === "delivered") {
      return <FaCheckDouble className="rekonic-check-icon" title="Delivered" />;
    }
    if (status === "sent") {
      return <FaCheck className="rekonic-check-icon" title="Sent" />;
    }
    return <FaClock className="rekonic-check-icon" title="Sending..." />;
  };

  return (
    <div className="rekonic-center-chat-area">
      {/* Chat Top Header */}
      <div className="rekonic-chat-main-header">
        <div className="rekonic-header-left">
          <div className={`rekonic-header-avatar ${isCrm ? "is-crm" : ""}`}>
            {displayName.slice(0, 2).toUpperCase()}
          </div>
          <div className="rekonic-header-details">
            <h3 className="rekonic-header-name">
              {displayName}
              {isCrm && (
                <span className="rekonic-crm-badge">
                  {activeConversation?.crm_customer?.customer_code || "CRM Customer"}
                </span>
              )}
            </h3>
            <span className="rekonic-header-sub">{phone}</span>
          </div>
        </div>

        {/* Action Shortcuts Header */}
        <div className="rekonic-header-actions">
          <button className="rekonic-btn-shortcut btn-quote" onClick={handleCreateQuotation} title="Generate Quotation for this Contact">
            <FaQuoteRight /> + Quote
          </button>
          <button className="rekonic-btn-shortcut btn-invoice" onClick={handleCreateInvoice} title="Generate Sale / Invoice for this Contact">
            <FaFileInvoice /> + Invoice
          </button>
          <button className="rekonic-btn-shortcut" onClick={handleCreateInquiry} title="Create Inquiry for this Contact">
            <FaQuestionCircle /> + Inquiry
          </button>
          <button className="rekonic-btn-shortcut" onClick={onOpenPullModal} title="Pull chat to your queue">
            <FaUserCheck /> PULL
          </button>
          <button className="rekonic-btn-shortcut" onClick={onOpenPushModal} title="Assign / Reassign chat to an employee">
            <FaExchangeAlt /> PUSH
          </button>
          {activeConversation?.is_active !== false ? (
            <button className="rekonic-btn-shortcut btn-close-chat" onClick={onCloseChat} title="Close / Resolve this chat">
              <FaTimes /> Close Chat
            </button>
          ) : (
            <button className="rekonic-btn-shortcut btn-reopen-chat" onClick={onReopenChat} title="Reopen this chat">
              <FaRedoAlt /> Reopen
            </button>
          )}
        </div>
      </div>

      {/* Messages Feed */}
      <div className="rekonic-messages-container">
        {isLoading && (
          <div style={{ textAlign: "center", color: "#64748b", fontSize: "12px", padding: "10px" }}>
            Loading messages...
          </div>
        )}

        {messages.length === 0 && !isLoading && (
          <div style={{ textAlign: "center", color: "#64748b", fontSize: "13px", margin: "auto" }}>
            No messages in this chat yet. Send a message or quick reply below.
          </div>
        )}

        {messages.map((msg, index) => {
          const isOutbound = msg.direction === "outbound";
          const isSystem = msg.direction === "system" || msg.msg_type === "system";

          if (isSystem) {
            return (
              <div key={msg.id || index} className="rekonic-msg-row system">
                <div className="rekonic-system-bubble">
                  {msg.body}
                </div>
              </div>
            );
          }

          const isImage = msg.msg_type === "image" || (msg.media_mime_type && msg.media_mime_type.startsWith("image"));
          const isPdf = msg.msg_type === "document" && msg.media_filename?.endsWith(".pdf");

          return (
            <div
              key={msg.id || index}
              className={`rekonic-msg-row ${isOutbound ? "outbound" : "inbound"}`}
            >
              <div className={`rekonic-bubble ${isOutbound ? "outbound" : "inbound"}`}>
                {/* Media rendering */}
                {isImage && msg.media_url && (
                  <img
                    src={msg.media_url}
                    alt="attachment"
                    className="rekonic-msg-image"
                    onClick={() => onImageClick && onImageClick(msg.media_url)}
                  />
                )}

                {isPdf && msg.media_url && (
                  <a
                    href={msg.media_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rekonic-msg-doc"
                  >
                    <FaFilePdf className="rekonic-doc-icon" />
                    <div className="rekonic-doc-name">{msg.media_filename || "Document.pdf"}</div>
                  </a>
                )}

                {/* Text Body */}
                {msg.body && <p className="rekonic-bubble-text">{msg.body}</p>}

                {/* Metadata */}
                <div className="rekonic-bubble-meta">
                  <span>{formatMessageTime(msg.timestamp)}</span>
                  {isOutbound && renderStatusIcon(msg.status)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default MessageArea;
